/**
 * 🆕 [Phase v13.59 — 2026-08-05] Bouton « Décocher Travaux terminés » (rollback).
 *
 * S'affiche UNIQUEMENT si l'utilisateur courant a la permission dynamique
 * `bt_travaux_termines_uncheck` (matrice DB `role_permissions`) et si le BT
 * est actuellement `termine === true`.
 *
 * Cohabite avec `<BtnTravauxTermines/>` (action inverse). Confirmation via
 * dialog identique en style + toast + refresh du parent.
 */
import axios from 'axios';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import { API_URL } from './utils';
import { useActionPermission } from '@/lib/PermissionsContext';

export function BtnDecocherTravauxTermines({ bonId, onDone }) {
  const canUncheck = useActionPermission('bt_travaux_termines_uncheck');
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!canUncheck) return null;

  const handleConfirm = async () => {
    setLoading(true);
    setShowConfirm(false);
    try {
      const res = await axios.post(
        `${API_URL}/api/crm/bons-travail/${bonId}/travaux-termines/uncheck`,
        null,
      );
      if (res.status !== 200) throw new Error(res?.data?.detail || 'Erreur serveur');
      toast.success('« Travaux terminés » a été retiré. Le BT repasse en cours.', {
        duration: 4000,
      });
      if (onDone) onDone();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erreur inconnue';
      toast.error(`Impossible de décocher : ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowConfirm(true)}
        disabled={loading}
        className="text-amber-700 border-amber-300 hover:bg-amber-50 gap-1.5 no-print"
        data-testid="btn-decocher-travaux-termines"
        title="Retirer le statut « Travaux terminés » — réservé au propriétaire (ou rôle autorisé)"
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</>
        ) : (
          <><RotateCcw className="w-4 h-4" /> Décocher terminé</>
        )}
      </Button>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              Décocher « Travaux terminés » ?
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <p className="text-slate-700">
              Voulez-vous vraiment <strong>retirer</strong> le statut
              « Travaux terminés » sur ce bon de travail&nbsp;?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <p className="font-semibold mb-1">Cette action va :</p>
              <ul className="space-y-0.5 list-disc list-inside text-amber-700">
                <li>Remettre le BT au statut <strong>« En cours »</strong>.</li>
                <li>Effacer la date de terminaison.</li>
                <li>Recalculer les heures réelles (défensif).</li>
                <li>Être tracée dans le journal d&apos;audit.</li>
              </ul>
            </div>
            <p className="text-xs text-slate-400">
              À utiliser uniquement si le statut a été activé par erreur.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirm(false)}
              disabled={loading}
              data-testid="btn-annuler-decocher"
            >
              Annuler
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
              data-testid="btn-confirmer-decocher-travaux-termines"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</>
              ) : (
                <><RotateCcw className="w-4 h-4" /> Oui, décocher</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BtnDecocherTravauxTermines;
